/**
 * Text fitting for SVG printables.
 *
 * SVG text does not wrap or shrink itself, so the widgets measure it. In the browser the
 * measurement is a canvas `measureText` call (see `useTextMeasure`); everywhere else —
 * the server render and the tests — it is an estimate from the standard Helvetica and
 * Times advance widths, so the first render is identical on both sides.
 *
 * Widths are in ems: multiply by the font size in millimetres for a width in millimetres.
 */

export type FontKind = "sans" | "serif" | "script";

export interface FontSpec {
  kind: FontKind;
  bold?: boolean;
  italic?: boolean;
}

/** Font stacks: system fonts only, so a PNG export and the print agree with the screen. */
export const FONT_STACKS: Record<FontKind, string> = {
  sans: "Helvetica, Arial, 'Helvetica Neue', sans-serif",
  serif: "Georgia, 'Times New Roman', Times, serif",
  script:
    "'Snell Roundhand', 'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
};

/** CSS `font` shorthand (without size) for canvas measuring and SVG attributes. */
export function fontStack(spec: FontSpec): string {
  return FONT_STACKS[spec.kind];
}

/** A function from text and font to a width in ems. */
export type TextMeasure = (text: string, font: FontSpec) => number;

/* Advance widths per 1000 em for ASCII 32–126 (Helvetica and Times-Roman AFM). */
const HELVETICA = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278,
  278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584,
  584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556,
  833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278,
  278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222,
  500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500,
  500, 334, 260, 334, 584,
];
const TIMES = [
  250, 333, 408, 500, 500, 833, 778, 180, 333, 333, 500, 564, 250, 333, 250,
  278, 500, 500, 500, 500, 500, 500, 500, 500, 500, 500, 278, 278, 564, 564,
  564, 444, 921, 722, 667, 667, 722, 611, 556, 722, 722, 333, 389, 722, 611,
  889, 722, 722, 556, 722, 667, 556, 611, 722, 722, 944, 722, 722, 611, 333,
  278, 333, 469, 500, 333, 444, 500, 444, 500, 444, 333, 500, 500, 278, 278,
  500, 278, 778, 500, 500, 500, 500, 333, 389, 278, 500, 500, 722, 500, 500,
  444, 480, 200, 480, 541,
];

const FIRST_CHAR = 32;
const LAST_CHAR = 126;
/** Anything outside ASCII (accents, symbols, other scripts) is measured as a wide letter. */
const OTHER_EM = 0.6;
const BOLD_FACTOR = 1.06;
const SCRIPT_FACTOR = 0.92;

function advance(table: readonly number[], char: string): number {
  const code = char.codePointAt(0) ?? 0;
  if (code >= FIRST_CHAR && code <= LAST_CHAR) {
    return (table[code - FIRST_CHAR] ?? 556) / 1000;
  }
  return OTHER_EM;
}

/** The estimate used when no canvas is available. */
export const estimateWidth: TextMeasure = (text, font) => {
  const table = font.kind === "sans" ? HELVETICA : TIMES;
  let total = 0;
  for (const char of text) {
    total += advance(table, char);
  }
  if (font.kind === "script") {
    total *= SCRIPT_FACTOR;
  }
  return font.bold ? total * BOLD_FACTOR : total;
};

/** Width in millimetres of `text` set at `sizeMm`. */
export function textWidthMm(
  measure: TextMeasure,
  text: string,
  font: FontSpec,
  sizeMm: number
): number {
  return measure(text, font) * sizeMm;
}

/**
 * The largest font size, between `minMm` and `maxMm`, at which `text` fits `widthMm`.
 * Text that does not fit even at the minimum comes back at the minimum.
 */
export function fitSize(
  measure: TextMeasure,
  text: string,
  font: FontSpec,
  widthMm: number,
  maxMm: number,
  minMm = 1
): number {
  const em = measure(text, font);
  if (em <= 0) {
    return maxMm;
  }
  return Math.max(minMm, Math.min(maxMm, widthMm / em));
}

/**
 * Greedy word wrap into lines no wider than `widthMm`. A single word wider than the line
 * stays on a line of its own (the caller can shrink it with `fitSize`).
 */
export function wrapText(
  measure: TextMeasure,
  text: string,
  font: FontSpec,
  sizeMm: number,
  widthMm: number
): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    const words = paragraph.split(" ").filter((word) => word.length > 0);
    if (words.length === 0) {
      lines.push("");
      continue;
    }
    let current = "";
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (current && textWidthMm(measure, candidate, font, sizeMm) > widthMm) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    lines.push(current);
  }
  return lines;
}

/** `text` cut with an ellipsis so it fits `widthMm` at `sizeMm` (never fewer than one letter). */
export function truncateToFit(
  measure: TextMeasure,
  text: string,
  font: FontSpec,
  sizeMm: number,
  widthMm: number
): string {
  if (textWidthMm(measure, text, font, sizeMm) <= widthMm) {
    return text;
  }
  const chars = Array.from(text);
  let end = chars.length;
  while (
    end > 1 &&
    textWidthMm(measure, `${chars.slice(0, end).join("")}…`, font, sizeMm) >
      widthMm
  ) {
    end -= 1;
  }
  return `${chars.slice(0, end).join("").trimEnd()}…`;
}

export interface FittedBlock {
  size: number;
  lines: string[];
}

/**
 * The largest size, from `maxMm` down to `minMm`, at which `text` wraps into at most
 * `maxLines` lines that each fit `widthMm`. Falls back to the minimum size (with the text
 * cut to `maxLines` lines, the last one ending in an ellipsis) when nothing fits.
 */
export function fitLines(
  measure: TextMeasure,
  text: string,
  font: FontSpec,
  widthMm: number,
  maxMm: number,
  minMm: number,
  maxLines: number
): FittedBlock {
  const STEP = 0.25;
  for (let size = maxMm; size >= minMm - 0.0001; size -= STEP) {
    const lines = wrapText(measure, text, font, size, widthMm);
    const fits = lines.every(
      (line) => textWidthMm(measure, line, font, size) <= widthMm + 0.01
    );
    if (lines.length <= maxLines && fits) {
      return { size: Math.round(size * 100) / 100, lines };
    }
  }
  const lines = wrapText(measure, text, font, minMm, widthMm);
  if (lines.length <= maxLines) {
    return {
      size: minMm,
      lines: lines.map((line) =>
        truncateToFit(measure, line, font, minMm, widthMm)
      ),
    };
  }
  const kept = lines.slice(0, maxLines);
  const last = `${kept.at(-1) ?? ""} ${lines.slice(maxLines).join(" ")}`.trim();
  kept[kept.length - 1] = truncateToFit(measure, last, font, minMm, widthMm);
  return { size: minMm, lines: kept };
}
